import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Drawer, Empty, InputNumber, List, Select, Space, Tag, message } from 'antd';
import { ShoppingCartOutlined, DeleteOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import axios from 'axios';
import config from '../../config';
import { jetons } from '../../components/ndoe/chartTheme';
import { getPanier, subscribe, removeLine, setQte, clearPanier, panierTotal, panierCount } from './panier';
import './panierBar.css';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');

// Barre « commande en cours » (Lot D) : transforme le panier catalogue en une
// vraie commande via POST commandePost + detail-commande (endpoints existants).
const PanierBar = () => {
  const userId = useSelector((s) => s.user?.currentUser?.id);
  const [lines, setLines] = useState(getPanier());
  const [open, setOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [idClient, setIdClient] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => subscribe(setLines), []);

  // Liste clientes chargée à la 1re ouverture du tiroir (lecture ouverte).
  const loadClients = useCallback(async () => {
    if (clients.length) return;
    try {
      const { data } = await axios.get(`${DOMAIN}/api/client/`);
      setClients(Array.isArray(data) ? data : []);
    } catch (e) {
      message.error('Impossible de charger les clientes.');
    }
  }, [clients.length]);

  useEffect(() => { if (open) loadClients(); }, [open, loadClients]);

  const clientOptions = useMemo(
    () => clients.map((c) => ({
      value: c.id,
      label: [c.nom, c.telephone].filter(Boolean).join(' · '),
    })),
    [clients],
  );

  const total = panierTotal(lines);
  const count = panierCount(lines);
  // Lu DANS le rendu, jamais au niveau module : au chargement du module le CSS
  // n'est pas garanti appliqué et le jeton reviendrait vide.
  const or = jetons().gold;

  const creer = async () => {
    if (!idClient) { message.warning('Choisissez une cliente.'); return; }
    if (!lines.length) return;
    setCreating(true);
    try {
      const { data } = await axios.post(`${DOMAIN}/api/commande/commandePost`, {
        id_client: idClient,
        user_cr: userId,
      });
      const idCommande = data?.id_commande;
      if (!idCommande) throw new Error('id_commande manquant');

      let ok = 0;
      let dup = 0;
      for (const l of lines) {
        try {
          await axios.post(`${DOMAIN}/api/commande/detail-commande`, {
            id_commande: idCommande,
            id_varianteProduit: l.id_varianteProduit,
            id_taille: l.id_taille,
            prix: l.prix,
            quantite: l.qte,
            user_cr: userId,
          });
          ok += 1;
        } catch (e) {
          if (e?.response?.status === 409) dup += 1; // déjà dans la commande
          else throw e;
        }
      }

      clearPanier();
      setIdClient(null);
      setOpen(false);
      message.success({
        content: (
          <span>
            Commande #{idCommande} créée — {ok} article{ok > 1 ? 's' : ''}
            {dup ? `, ${dup} déjà présent${dup > 1 ? 's' : ''}` : ''}.{' '}
            <a href={`/listeDetailView/${idCommande}`}>Ouvrir la commande</a>
          </span>
        ),
        duration: 6,
      });
    } catch (e) {
      message.error(e?.response?.data?.message || e?.response?.data?.error || 'Création de la commande échouée.');
    } finally {
      setCreating(false);
    }
  };

  if (!lines.length) return null;

  return (
    <>
      <div
        className="ndoe-panier-fab"
        style={{
          background: '#071c3d', color: '#fff', borderRadius: 999,
          padding: '10px 18px', boxShadow: '0 8px 24px rgba(7,28,61,.28)',
          display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer',
        }}
        onClick={() => setOpen(true)}
        role="button"
      >
        {/* key=count : remonte le nœud à chaque ajout → l'animation « bump » se rejoue. */}
        <span key={count} className="ndoe-cart-bump">
          {/* Le badge et le bouton étaient peints en #c9a227 : l'or de l'ANCIEN
              jeu de jetons, celui qui perdait la cascade et a été supprimé au
              lot 1. Cette barre affichait donc un or que plus rien d'autre dans
              l'app n'utilisait, à deux pixels d'une interface en #c28a27.
              `color` et `style` de Badge/Button vont dans un attribut inline :
              antd ne résout pas var(--ndoe-gold), d'où la lecture explicite du
              jeton — même principe que le pont des graphiques. */}
          <Badge count={count} size="small" color={or} offset={[2, -2]}>
            <ShoppingCartOutlined style={{ fontSize: 20, color: '#fff' }} />
          </Badge>
        </span>
        <span style={{ fontWeight: 600 }}>Commande en cours · {fmt(total)}</span>
        <Button size="small" type="primary" style={{ background: or, borderColor: or }}>
          Finaliser
        </Button>
      </div>

      <Drawer
        title="Commande en cours"
        placement="right"
        width={460}
        open={open}
        onClose={() => setOpen(false)}
        destroyOnClose
      >
        {lines.length === 0 ? (
          <Empty description="Panier vide" />
        ) : (
          <>
            <List
              size="small"
              dataSource={lines}
              rowKey={(l) => l.id_varianteProduit}
              renderItem={(l) => (
                <List.Item
                  actions={[
                    <InputNumber inputMode="decimal"
                      key="q"
                      min={1}
                      value={l.qte}
                      onChange={(v) => setQte(l.id_varianteProduit, v)}
                      style={{ width: 64 }}
                    />,
                    <Button
                      key="d"
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeLine(l.id_varianteProduit)}
                    />,
                  ]}
                >
                  <List.Item.Meta
                    title={<span style={{ color: '#071c3d' }}>{l.nom_produit}</span>}
                    description={
                      <Space size={6} wrap>
                        {l.marque && <span>{l.marque}</span>}
                        {l.taille != null && <Tag>P.{l.taille}</Tag>}
                        {l.couleur && <span style={{ color: '#8a93a3' }}>{l.couleur}</span>}
                        <span>· {fmt(l.prix)}</span>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />

            <div style={{ borderTop: '1px solid #eee8de', marginTop: 12, paddingTop: 12 }}>
              <div style={{ marginBottom: 8, fontWeight: 600, color: '#071c3d' }}>
                Total : {fmt(total)} · {count} paire{count > 1 ? 's' : ''}
              </div>
              <Select
                showSearch
                allowClear
                placeholder="Cliente…"
                style={{ width: '100%', marginBottom: 12 }}
                value={idClient}
                onChange={setIdClient}
                options={clientOptions}
                optionFilterProp="label"
                filterOption={(input, opt) => (opt?.label || '').toLowerCase().includes(input.toLowerCase())}
              />
              <Space>
                <Button
                  type="primary"
                  loading={creating}
                  onClick={creer}
                  style={{ background: '#071c3d', borderColor: '#071c3d' }}
                >
                  Créer la commande
                </Button>
                <Button onClick={() => { clearPanier(); setOpen(false); }}>Vider</Button>
              </Space>
            </div>
          </>
        )}
      </Drawer>
    </>
  );
};

export default PanierBar;
